import React from 'react';
/**
 * Utility functions for creating clickable phone and email links
 */

interface PhoneLinkProps {
  phoneNumber: string;
  displayText?: string;
  className?: string;
}

interface EmailLinkProps {
  email: string;
  subject?: string;
  displayText?: string;
  className?: string;
}

/**
 * Creates a clickable phone link component props
 */
export const createPhoneLink = ({ phoneNumber, displayText, className }: PhoneLinkProps) => {
  // Clean phone number for href (remove spaces, dashes, parentheses)
  const cleanPhone = phoneNumber.replace(/[()\s-]/g, '');
  
  return {
    href: `tel:${cleanPhone}`,
    children: displayText || phoneNumber,
    className: `hover:underline ${className || ''}`.trim(),
  };
};

/**
 * Creates a clickable email link component props
 */
export const createEmailLink = ({ email, subject, displayText, className }: EmailLinkProps) => {
  const href = subject 
    ? `mailto:${email}?subject=${encodeURIComponent(subject)}`
    : `mailto:${email}`;
    
  return {
    href,
    children: displayText || email,
    className: `hover:underline ${className || ''}`.trim(),
  };
};

/**
 * Creates WhatsApp link
 */
export const createWhatsAppLink = (phoneNumber: string, message?: string) => {
  const cleanPhone = phoneNumber.replace(/[()\s+-]/g, '');
  const encodedMessage = message ? encodeURIComponent(message) : '';
  const suffix = encodedMessage ? `?text=${encodedMessage}` : '';
  return `https://wa.me/${cleanPhone}${suffix}`;
};

/**
 * Formats phone number for display (adds country code if missing)
 */
export const formatPhoneDisplay = (phone: string): string => {
  // If phone doesn't start with +, assume it's Italian
  if (!phone.startsWith('+')) {
    return `+39 ${phone}`;
  }
  return phone;
};

/**
 * Component for clickable phone links
 */
export const PhoneLink = ({ phoneNumber, displayText, className, ...props }: PhoneLinkProps & React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
  const linkProps = createPhoneLink({ phoneNumber, displayText, className });
  return React.createElement('a', { ...linkProps, ...props });
};

/**
 * Component for clickable email links
 */
export const EmailLink = ({ email, subject, displayText, className, ...props }: EmailLinkProps & React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
  const linkProps = createEmailLink({ email, subject, displayText, className });
  return React.createElement('a', { ...linkProps, ...props });
};